import { unref as vueUnref } from 'vue';
import RegionalUtils from '@/core/utils/RegionalUtils';

class StringUtils {
	/**
	 * Проверяет, является ли значение строкой
	 */
	static isString(value: unknown): value is string {
		value = vueUnref(value);
		return typeof value === 'string';
	}

	/**
	 * Проверяет, можно ли значение преобразовать в строку
	 */
	static isStringLike(value: unknown): value is string | number {
		value = vueUnref(value);
		return typeof value === 'number' || typeof value === 'string';
	}

	/**
	 * Преобразует значение в строку
	 */
	static toString(value: unknown): string {
		value = vueUnref(value);
		if (!this.isStringLike(value)) {
			return '';
		}
		return typeof value === 'string' ? value : value.toString();
	}

	/**
	 * Проверяет, что значение является непустой строкой
	 */
	static isNotEmptyString(value: unknown): value is string {
		value = vueUnref(value);
		return this.isString(value) && value.trim().length > 0;
	}

	/**
	 * Очищает строку от всех нецифровых символов
	 */
	static cleanPhone(value: unknown): string {
		const stringValue = this.toString(value);
		return stringValue.replace(/\D/g, '');
	}

	/**
	 * Дополняет строку указанным символом до заданной длины
	 * @param {any} input - Значение для дополнения (будет преобразовано в строку)
	 * @param {number} length - Требуемая длина строки
	 * @param {string} fillChar - Символ для заполнения (по умолчанию '0')
	 * @returns {string|null} Строка с ведущими символами или null если input некорректный
	 */
	static leftPad(input: unknown, length: number = 2, fillChar: string = '0'): string | null {
		if (input == null) {
			return null;
		}

		const stringValue = this.toString(input);
		return stringValue.padStart(length, fillChar);
	}

	/**
	 * Преобразует строку в camelCase (убирает пробелы, дефисы, подчеркивания)
	 *
	 * @param {unknown} str - Строка для преобразования
	 * @returns {string | null} Строка в camelCase или null если входное значение не строка
	 * @example
	 * camelize('hello-world')        // 'helloWorld'
	 * @example
	 * camelize('hello_world')        // 'helloWorld'
	 * @example
	 * camelize('hello world')        // 'helloWorld'
	 * @example
	 * camelize('hello-world_test')   // 'helloWorldTest'
	 * @example
	 * camelize('Hello-World')        // 'helloWorld'
	 * @example
	 * camelize('test--multiple---dashes') // 'testMultipleDashes'
	 * @example
	 * camelize(123)                  // null
	 * @example
	 * camelize(null)                 // null
	 */
	static camelize(str: unknown): string | null {
		if (!this.isString(str)) {
			return null;
		}

		return str
			.replace(/[-_\s]+(.)?/g, (_, char) => char ? char.toUpperCase() : '')
			.replace(/^[A-Z]/, match => match.toLowerCase());
	}

	/**
	 * Делает первую букву строки заглавной
	 */
	static capitalize(str: unknown): string | null {
		str = vueUnref(str);
		if (!this.isString(str) || str.length === 0) {
			return null;
		}

		return str.charAt(0).toUpperCase() + str.slice(1);
	}

	/**
	 * Форматирует номера/числа разрядами с разделителем для улучшения читаемости
	 * @param {number|string} value - Значение для форматирования
	 * @param {number} range - Размер разряда (по умолчанию 4)
	 * @param {string} delimiter - Разделитель (по умолчанию '-')
	 * @returns {string} Отформатированная строка или пустая строка если параметры некорректные
	 */
	static formatWithDelimiter(
		value: unknown = null,
		range: number = 4,
		delimiter: string = '-'
	): string {
		/** Обращение через StringUtils, а не через this, потому что эти методы используются как фильтры для вью, но вью
		 * решил почему то контексты не сохранять, и this будет равен undefined */
		if (!StringUtils.isStringLike(value) || typeof range !== 'number' || !StringUtils.isString(delimiter)) {
			return '';
		}

		const string = StringUtils.toString(value);
		const array = string.split('');
		const length = array.length;

		if (length <= range) return string;

		for (let index = length - 1; index > -1; index--) {
			const reversePosition = length - index;
			if (reversePosition % range === 0 && reversePosition < length) {
				array[index] = delimiter + array[index];
			}
		}

		return array.join('');
	}

	/**
	 * Удаляет все нецифровые символы из строки
	 */
	static clearPhone(value: unknown): string {
		const stringValue = this.toString(value);
		return stringValue.replace(/\D/g, '');
	}

	/**
	 * Форматирует телефонный номер универсальным способом
	 * Использует форматирование справа налево по шаблону: XX-XX-XXX (XXX) XXX+
	 */
	static formatPhoneNumber(value: unknown): string | null {
		// Приводим к строке
		const valueStr = StringUtils.toString(value);
		if (!valueStr) return null;

		// Очищаем от всего кроме цифр
		const cleanValue = StringUtils.clearPhone(valueStr);
		if (!cleanValue) return null;

		// Разворачиваем цифры справа налево для форматирования
		const symbolArr = cleanValue.split('').reverse();

		// Строим результат справа налево по шаблону: XX-XX-XXX (XXX) XXX+
		const result: string[] = [];

		// Добавляем последние 7 цифр в формате: XX-XX-XXX
		result.push(
			symbolArr[0], symbolArr[1], '-',        // Последние 2 цифры + дефис
			symbolArr[2], symbolArr[3], '-',        // Следующие 2 цифры + дефис
			symbolArr[4], symbolArr[5], symbolArr[6] // Следующие 3 цифры
		);

		// Убираем обработанные 7 цифр из массива
		symbolArr.splice(0, 7);

		// Если остались еще цифры - добавляем их в формате: (XXX)
		if (symbolArr.length > 3) {
			result.push(
				' ', ')',
				...symbolArr.splice(0, 3),
				'(', ' '
			);
		}

		// Добавляем оставшиеся цифры (обычно код страны)
		result.push(...symbolArr);

		// Если номер длиннее 10 цифр И не начинается с 8800 - добавляем знак +
		if (cleanValue.length > 10 && !cleanValue.startsWith('8')) {
			result.push('+');
		}

		// Разворачиваем результат обратно в нормальном порядке
		return result.reverse().join('');
	}

	/**
	 * Выбирает правильную форму слова для русского языка
	 * @param {number} number - Число для которого выбираем форму слова
	 * @param {string} oneForm - Форма для единственного числа (1 день)
	 * @param {string} fewForm - Форма для 2-4 (2 дня)
	 * @param {string} manyForm - Форма для 5+ (5 дней)
	 * @returns {string|null} Подходящая форма слова или null если что-то неправильно
	 */
	static pluralizeRu(
		number: number,
		oneForm: string,
		fewForm: string,
		manyForm: string
	): string | null {
		return RegionalUtils.ru.pluralize(number, oneForm, fewForm, manyForm);
	}

	/**
	 * Функция, форматирующая ФИО
	 */
	static formatPersonName(value: unknown, format: 'short' | 'full' = 'full'): string | null {
		value = vueUnref(value);
		if (!value || typeof value !== 'string' || value.length === 0) return null;

		switch (format) {
			case 'short':
				return value.split(' ')[0];
			default:
				return value;
		}
	}
}

export default StringUtils;
